import { router } from "@inertiajs/react";
import type { Subscription } from "@rails/actioncable";
import * as React from "react";
import { toast } from "sonner";

import { getConsumer } from "@/lib/cable";
import type { NotificationPayload, SerializedNotification } from "@/types";

// =============================================================================
// Constants
// =============================================================================

const MAX_NOTIFICATIONS_IN_DROPDOWN = 10;
const FETCH_ENDPOINT = "/notifications/unread";

// =============================================================================
// Types
// =============================================================================

interface NotificationsResponse {
  notifications: SerializedNotification[];
  unread_count: number;
}

interface NotificationsContextValue {
  /** List of recent notifications (max 10) */
  notifications: SerializedNotification[];
  /** Count of unread notifications */
  unreadCount: number;
  /** Whether initial fetch is in progress */
  isLoading: boolean;
  /** Mark a single notification as read (optimistic update) */
  markAsRead: (id: number) => void;
  /** Mark all notifications as read (optimistic update) */
  markAllAsRead: () => void;
  /** Refresh notifications from the server */
  refresh: () => void;
}

// =============================================================================
// Context
// =============================================================================

const NotificationsContext = React.createContext<NotificationsContextValue | null>(null);

// =============================================================================
// Hook
// =============================================================================

/**
 * Access the centralized notifications context.
 * Must be used within a NotificationsProvider.
 */
function useNotifications(): NotificationsContextValue {
  const context = React.useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationsProvider.");
  }
  return context;
}

// =============================================================================
// Provider
// =============================================================================

interface NotificationsProviderProps {
  children: React.ReactNode;
}

function NotificationsProvider({ children }: NotificationsProviderProps) {
  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------
  const [notifications, setNotifications] = React.useState<SerializedNotification[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);

  const subscriptionRef = React.useRef<Subscription | null>(null);

  // ---------------------------------------------------------------------------
  // Fetch Notifications
  // ---------------------------------------------------------------------------
  const fetchNotifications = React.useCallback(async () => {
    try {
      const response = await fetch(FETCH_ENDPOINT);

      // Check if response is HTML (redirect to login, error page, etc.)
      const contentType = response.headers.get("content-type");
      if (!response.ok || !contentType?.includes("application/json")) {
        // Silently fail - likely unauthenticated or server error
        setNotifications([]);
        setUnreadCount(0);
        return;
      }

      const data = (await response.json()) as NotificationsResponse;
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch (error) {
      console.error("[NotificationsProvider] Failed to fetch:", error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Handle Incoming Notification (from ActionCable)
  // ---------------------------------------------------------------------------
  const handleReceivedNotification = React.useCallback((data: NotificationPayload) => {
    console.debug("[NotificationsProvider] Received:", data);

    const newNotification: SerializedNotification = {
      id: data.id,
      type: data.type,
      title: data.title,
      body: data.body,
      url: data.url,
      read_at: null,
      created_at: data.created_at,
    };

    // Add to state (prepend, limit to MAX)
    setNotifications((prev) => [
      newNotification,
      ...prev.slice(0, MAX_NOTIFICATIONS_IN_DROPDOWN - 1),
    ]);
    setUnreadCount((prev) => prev + 1);

    // Show toast
    toast(data.title, {
      description: data.body,
      action: data.url
        ? {
            label: "Ver",
            onClick: () => {
              router.visit(data.url!);
            },
          }
        : undefined,
    });
  }, []);

  // ---------------------------------------------------------------------------
  // ActionCable Subscription
  // ---------------------------------------------------------------------------
  React.useEffect(() => {
    void fetchNotifications();

    const consumer = getConsumer();
    subscriptionRef.current = consumer.subscriptions.create<NotificationPayload>(
      "NotificationsChannel",
      {
        connected() {
          console.debug("[NotificationsProvider] Connected to NotificationsChannel");
        },
        disconnected() {
          console.debug("[NotificationsProvider] Disconnected from NotificationsChannel");
        },
        rejected() {
          console.warn("[NotificationsProvider] Subscription rejected by server");
        },
        received(data) {
          handleReceivedNotification(data);
        },
      },
    );

    return () => {
      console.debug("[NotificationsProvider] Unsubscribing from NotificationsChannel");
      subscriptionRef.current?.unsubscribe();
      subscriptionRef.current = null;
    };
  }, [fetchNotifications, handleReceivedNotification]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  /**
   * Mark a single notification as read.
   * Uses optimistic update - updates UI immediately, then syncs with server.
   */
  const markAsRead = React.useCallback(
    (id: number) => {
      // Optimistic update
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));

      // Sync with server
      router.patch(
        `/notifications/${id}/mark_as_read`,
        {},
        {
          preserveScroll: true,
          onError: () => {
            // Revert on error - refetch to get correct state
            void fetchNotifications();
          },
        },
      );
    },
    [fetchNotifications],
  );

  /**
   * Mark all notifications as read.
   * Uses optimistic update - updates UI immediately, then syncs with server.
   */
  const markAllAsRead = React.useCallback(() => {
    const now = new Date().toISOString();
    // Optimistic update
    setNotifications((prev) => prev.map((n) => ({ ...n, read_at: n.read_at ?? now })));
    setUnreadCount(0);

    // Sync with server
    router.patch(
      "/notifications/mark_all_as_read",
      {},
      {
        preserveScroll: true,
        onError: () => {
          // Revert on error - refetch to get correct state
          void fetchNotifications();
        },
      },
    );
  }, [fetchNotifications]);

  // ---------------------------------------------------------------------------
  // Context Value (memoized)
  // ---------------------------------------------------------------------------
  const contextValue = React.useMemo<NotificationsContextValue>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      markAsRead,
      markAllAsRead,
      refresh: fetchNotifications,
    }),
    [notifications, unreadCount, isLoading, markAsRead, markAllAsRead, fetchNotifications],
  );

  return (
    <NotificationsContext.Provider value={contextValue}>
      {children}
    </NotificationsContext.Provider>
  );
}

export { NotificationsProvider, useNotifications };
export type { NotificationsContextValue };
